import { Box, Button, Card, Heading, NavList } from '@deckee/deck-hand';
import React, { FC } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { captureEvent } from '../../../services/event-capture';
import { isVesselFloatPlanReady } from '../../../services/log-trips/float-plan';
import {
  ROUTE_BOAT_DETAILS_CREATE,
  ROUTE_BOAT_DETAILS_EDIT,
} from '../../../services/route/route-names';
import { transitionToNextRoute } from '../../../services/route/route-navigation';
import { useAppDispatch } from '../../../services/store';
import { setVessel } from '../../../services/store/modules/float-plan';
import { selectPlanIsLoggingOn } from '../../../services/store/modules/plan';
import { isValidLoggedOnVessel } from '../../../services/store/modules/vessels';
import { Vessel } from '../../../services/store/modules/vessels/models';
import BasicDrawerLayout from '../../layouts/BasicDrawerLayout/BasicDrawerLayout';
import { LayoutBody } from '../../layouts/components/LayoutBody/LayoutBody';
import VesselListItem from './components/VesselListItem';
import useVessels from './hooks/use-vessels';

interface SelectVesselPageProps {
  onSelectVessel: (vessel: Vessel | undefined) => void;
  onBack: () => void;
}

const SelectVesselPage: FC<SelectVesselPageProps> = ({
  onSelectVessel,
  onBack,
}) => {
  const dispatch = useAppDispatch();
  const { vessels, selectVessel: onVesselSelect } = useVessels();
  const history = useHistory();
  const isLoggingOn = useSelector(selectPlanIsLoggingOn);

  const handleAddBoatOnClick = async () => {
    captureEvent({
      type: 'float: vessel',
      payload: {
        selection: 'added new',
      },
    });

    transitionToNextRoute(ROUTE_BOAT_DETAILS_CREATE);
  };

  const handleAddVessel = () => {
    captureEvent({
      type: 'select vessel feature: click add vessel',
      payload: {},
    });
    history.push('/maps/settings/account/boats/new');
  };

  const handleOnSelect = (vessel: Vessel | undefined) => {
    if (vessel) {
      // set selected vessel in store
      onVesselSelect(vessel);
      dispatch(setVessel(vessel));

      if (
        (isLoggingOn && !isValidLoggedOnVessel(vessel)) ||
        !isVesselFloatPlanReady(vessel, isLoggingOn)
      ) {
        transitionToNextRoute(ROUTE_BOAT_DETAILS_EDIT, {
          id: vessel.id,
        });
      } else {
        captureEvent({
          type: 'select vessel feature: continue',
          payload: {},
          sendToAmplitude: true,
        });
        onSelectVessel(vessel);
      }
    }
  };

  const handleEditVessel = (vessel: Vessel | undefined) => {
    if (vessel) {
      captureEvent({
        type: 'select vessel feature: click edit vessel',
        payload: {
          vessel,
        },
      });
      transitionToNextRoute(ROUTE_BOAT_DETAILS_EDIT, {
        id: vessel.id,
      });
    }
  };

  const showDescription = (vessel: Vessel): React.ReactNode => {
    if (
      (isLoggingOn && !isValidLoggedOnVessel(vessel)) ||
      !isVesselFloatPlanReady(vessel, isLoggingOn)
    ) {
      return (
        <Box color="danger" fontSize="1">
          More details needed
        </Box>
      );
    }
    return null;
  };

  return (
    <BasicDrawerLayout
      showMenuBar={false}
      heading="Select watercraft"
      onClose={null}
      onBack={onBack}
    >
      <LayoutBody>
        <Card>
          <Card.Body>
            <NavList edge grouped>
              {vessels.map((vessel) => (
                <VesselListItem
                  key={vessel.id}
                  vessel={vessel}
                  editable
                  onEdit={() => handleEditVessel(vessel)}
                  onClick={() => handleOnSelect(vessel)}
                  note={showDescription(vessel)}
                />
              ))}
            </NavList>

            {vessels.length === 0 && (
              <Heading
                color="tertiary"
                textAlign="center"
                level={6}
                onClick={handleAddBoatOnClick}
              >
                Add a new watercraft
              </Heading>
            )}
            <Button.Group wide mt="3">
              <Button wide onClick={handleAddVessel} outlined>
                Add watercraft
              </Button>
            </Button.Group>
          </Card.Body>
        </Card>
      </LayoutBody>
    </BasicDrawerLayout>
  );
};

export default SelectVesselPage;
